/**
 * Input — Modernize-style form input.
 *
 * Phase 12 refresh: was translucent glass (bg-surface/60 +
 * backdrop-blur + brand glow on focus). Modernize uses solid slate
 * surfaces that brighten to white on focus — cleaner and more
 * legible next to refreshed Card / DataTable surfaces.
 *
 *   - Rest: bg-slate-50 (dark: slate-800) + slate-200 border
 *   - Focus: bg-white + brand-400 border + subtle brand-500/20 ring
 *   - Error: rose-50 bg + rose-300 border + rose-500 ring
 *
 * Supports optional prefix/suffix slots (used for search icons,
 * unit labels, etc.) with a matching soft-gray surround.
 */

import * as React from "react";
import { cva } from "class-variance-authority";
import type { VariantProps } from "class-variance-authority";

import { cn } from "@/lib/utils";

const inputVariants = cva(
  "flex h-10 w-full rounded-xl border px-3.5 py-2 text-sm transition-colors " +
    "file:border-0 file:bg-transparent file:text-sm file:font-medium " +
    "placeholder:text-slate-400 " +
    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/30 " +
    "disabled:cursor-not-allowed disabled:opacity-50",
  {
    variants: {
      variant: {
        default:
          "border-slate-300 bg-white text-slate-900 placeholder:text-slate-500 hover:border-slate-400 focus-visible:border-brand-500 " +
          "dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100 dark:placeholder:text-slate-500 dark:hover:border-slate-600 dark:focus-visible:border-brand-400",
        error:
          "border-rose-300 bg-rose-50 text-rose-900 placeholder:text-rose-400 focus-visible:ring-rose-500/30 focus-visible:border-rose-500 " +
          "dark:border-rose-500/40 dark:bg-rose-500/10 dark:text-rose-200",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  },
);

export interface InputProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, "prefix">,
    VariantProps<typeof inputVariants> {
  prefix?: React.ReactNode;
  suffix?: React.ReactNode;
}

const Input = React.forwardRef<HTMLInputElement, InputProps>(
  ({ className, variant, type, prefix, suffix, ...props }, ref) => {
    if (prefix || suffix) {
      const shellBase =
        "flex h-10 w-full items-center rounded-xl border text-sm transition-colors " +
        "focus-within:outline-none focus-within:ring-2";
      const shellTone =
        variant === "error"
          ? "border-rose-300 bg-rose-50 focus-within:border-rose-500 focus-within:ring-rose-500/30 " +
            "dark:border-rose-500/40 dark:bg-rose-500/10"
          : "border-slate-300 bg-white hover:border-slate-400 focus-within:border-brand-500 focus-within:ring-brand-500/30 " +
            "dark:border-slate-700 dark:bg-slate-900 dark:hover:border-slate-600 dark:focus-within:border-brand-400";
      const affixTone =
        variant === "error"
          ? "text-rose-500"
          : "text-slate-400";
      const inputTone =
        variant === "error"
          ? "text-rose-900 placeholder:text-rose-400 dark:text-rose-200"
          : "text-slate-900 placeholder:text-slate-400 dark:text-slate-100";

      return (
        <div
          className={cn(
            shellBase,
            shellTone,
            props.disabled && "cursor-not-allowed opacity-50",
            className,
          )}
        >
          {prefix && (
            <span className={cn("flex items-center pl-3.5 [&_svg]:size-4", affixTone)}>
              {prefix}
            </span>
          )}
          <input
            type={type}
            className={cn(
              "flex h-full w-full bg-transparent px-3.5 py-2 text-sm focus-visible:outline-none disabled:cursor-not-allowed",
              inputTone,
            )}
            ref={ref}
            {...props}
          />
          {suffix && (
            <span className={cn("flex items-center pr-3.5 [&_svg]:size-4", affixTone)}>
              {suffix}
            </span>
          )}
        </div>
      );
    }

    return (
      <input
        type={type}
        className={cn(inputVariants({ variant, className }))}
        ref={ref}
        {...props}
      />
    );
  },
);
Input.displayName = "Input";

export { Input, inputVariants };
